import React, { useEffect, useState, createContext, useContext } from 'react';
import { Auth } from 'aws-amplify';

import {
  Account,
  AccountUser,
  PartialEditableAccount,
  PartialEditableUser,
  User,
  TermsAndConditions,
} from 'api-bible-shared-lib/utils/types';
import { useAuth } from './Auth';

import * as portalApi from '../utils/api';

export interface AccountUserContextParams {
  user: User | undefined;
  account: Account | undefined;
  signOut: () => Promise<void>;
  updateUser: (updates: PartialEditableUser) => Promise<User>;
  updateAccount: (updates: PartialEditableAccount) => Promise<Account>;
  verifyEmail: (code: string) => Promise<void>;
  sendVerificationCode: () => Promise<void>;
  registrationCompleted: boolean;
  termsAndConditions: TermsAndConditions | undefined;
  acceptedLatestTermsAndConditions: boolean;
}

const defaultAccountUserContextParams: AccountUserContextParams = {
  user: undefined,
  account: undefined,
  signOut: () => Promise.reject(new Error('no active account user')),
  updateUser: () => Promise.reject(new Error('no active account user')),
  updateAccount: () => Promise.reject(new Error('no active account user')),
  verifyEmail: () => Promise.reject(new Error('no active account user')),
  sendVerificationCode: () => Promise.reject(new Error('no active account user')),
  registrationCompleted: false,
  termsAndConditions: undefined,
  acceptedLatestTermsAndConditions: false,
};

export const AccountUserContext = createContext<AccountUserContextParams>(
  defaultAccountUserContextParams,
);

export const useAccountUser = () => useContext(AccountUserContext);

export const UserProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const { authData, authRefresh } = useAuth();
  const authAttributes = authData?.attributes || {};
  const userId = authAttributes?.sub || '';
  const accountId = authAttributes?.['custom:account_id'] || '';
  const emailVerified = authAttributes?.email_verified || false;
  const email = authAttributes?.email;
  const group = authData?.signInUserSession.accessToken.payload['cognito:groups'];
  const isSuperAdmin = group ? group.includes('super-admins-group') : false;

  const [account, setAccount] = useState<Account | undefined>();
  const [user, setUser] = useState<User | undefined>();

  const signOut = async () => {
    try {
      await Auth.signOut();
      setUser(undefined);
      setAccount(undefined);
      window.location.reload();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const sendVerificationCode = async () => Auth.verifyCurrentUserAttribute('email');

  // eslint-disable-next-line consistent-return
  const verifyEmail = async (code: string) => {
    await Auth.verifyCurrentUserAttributeSubmit('email', code); // this will thrown an error if incorrect code
    if (authRefresh) return authRefresh(); // refresh auth data if verified code was correct
  };

  // INFO: API endpoint should receive the update diff, fetch the dynamo profile
  // record, merge the two objects, save, and return the full new profile
  const updateAccountUser = async (updates: Partial<AccountUser>): Promise<AccountUser> => {
    if (!user) throw new Error('user must be signed in to update');

    const currAccountUser: AccountUser = { name: user.name || '' };
    if (user.photo) currAccountUser.photo = user.photo;

    const updatesMakeChanges = (Object.keys(updates) as Array<keyof typeof updates>).some(
      (key) => updates[key] !== currAccountUser[key],
    );

    if (updatesMakeChanges) {
      const res = await portalApi.putAccountUser({
        accountId: user.accountId,
        userId: user.id,
        data: updates,
      });
      return res.user;
    }
    return { ...currAccountUser };
  };

  const updateUser = async (updates: PartialEditableUser): Promise<User> => {
    if (!user) throw new Error('User must be signed in');

    const { oldPassword = '', newPassword = '', ...newProfile } = updates;
    const userConstants = {
      id: user.id,
      accountId: user.accountId,
      isSuperAdmin: user.isSuperAdmin,
    };

    const newSavedProfile = await updateAccountUser(newProfile);
    if (oldPassword && newPassword) await Auth.changePassword(authData, oldPassword, newPassword);

    const emailUpdated = newProfile.email && newProfile.email !== user.email;
    if (emailUpdated && authRefresh) await authRefresh();

    const newUser = {
      ...userConstants,
      ...newSavedProfile,
      email: newProfile.email || user.email,
      emailVerified: authAttributes?.email_verified || false,
    };
    setUser(newUser);
    return newUser;
  };

  const updateAccount = async (updates: PartialEditableAccount): Promise<Account> => {
    const editedAccount = await portalApi.putAccount({ accountId, data: updates });
    setAccount(editedAccount.account);
    return editedAccount.account;
  };

  useEffect(() => {
    if (accountId && userId) {
      Promise.all([
        portalApi.getAccount({ accountId }),
        portalApi.getAccountUser({ accountId, userId }),
      ])
        .then(([accountRes, accountUserRes]) => {
          setAccount(accountRes.account);
          setUser({
            id: userId,
            accountId,
            email,
            emailVerified,
            isSuperAdmin,
            name: accountUserRes?.user?.name || '',
            photo: accountUserRes?.user?.photo || '',
          });
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
          // TODO: redirect to some kind of error page?
        });
    }
  }, [email, emailVerified, accountId, isSuperAdmin, userId]);

  // Check account.use to ensure accounts migrated during migration would be forced to update here
  // since it is not in the legacy data
  const registrationCompleted = Boolean(user?.name) && Boolean(account) && Boolean(account?.use);

  const [termsAndConditions, setTermsAndConditions] = useState({
    id: '',
    content: '',
    log: '',
    createdAt: '',
  });
  useEffect(() => {
    portalApi
      .getTermsAndConditions()
      .then((tcs) => {
        setTermsAndConditions(tcs);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  }, []);

  const acceptedLatestTermsAndConditions =
    termsAndConditions.id === account?.acceptedTermsAndConditions?.id;

  return (
    <AccountUserContext.Provider
      value={
        user && account
          ? {
              user,
              account,
              signOut,
              updateUser,
              updateAccount,
              verifyEmail,
              sendVerificationCode,
              registrationCompleted,
              termsAndConditions,
              acceptedLatestTermsAndConditions,
            }
          : defaultAccountUserContextParams
      }
    >
      {children}
    </AccountUserContext.Provider>
  );
};
